import type { FC } from 'react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Alert } from '@interstate/components/Alert';
import { Box } from '@interstate/components/Box';
import { Button } from '@interstate/components/Button';
import { Action } from '@interstate/components/Action';
import { FilterChip, LinkChip } from '@interstate/components/Chip';
import { FunnelIcon, LockClosedIcon } from '@interstate/components/Icons';
import { Spinner } from '@interstate/components/Spinner';
import { Typography } from '@interstate/components/Typography';
import { featureToggleSelectors } from '@makemydeal/dr-shared-store';
import { formatLenderProgramDisplay } from '@makemydeal/dr-shared-ui-utils';
import type { StateTree } from '@makemydeal/dr-dash-types';
import * as offerReduxSelectors from '@makemydeal/dr-dash-store/selectors/offerRedux';
import * as firstPencilSelectors from '@makemydeal/dr-dash-store/selectors/firstPencilSelectors';
import * as firstPencilActionCreators from '@makemydeal/dr-dash-store/actions/firstPencilActionCreators';
import * as firstPencilProgramActionCreators from '@makemydeal/dr-dash-store/actions/firstPencilProgramActionCreators';
import { logFirstPencilRrDisplayed } from '@makemydeal/dr-dash-store/utils/firstPencilTelemetry';

import { ProgramsFiltersSlideOut } from '../Programs/ProgramsFiltersSlideOut';
import type { ProgramFilters } from '../Programs/ProgramsFilters.types';
import { DEFAULT_FILTERS, MAX_VISIBLE_FILTER_CHIPS, NO_RESULTS_MESSAGE } from '../Programs/ProgramsFilters.types';
import {
    extractAllFilterOptions,
    filterPrograms,
    getAppliedFilterTags,
    hasActiveFilters,
    removeFilterValue
} from '../Programs/ProgramsFilters.utils';
import {
    ALL_CREDIT_TIERS_VALUE,
    getCustomerCreditScore,
    getCustomerCreditTier,
    getScoreForTier,
    isValidScore
} from '../Programs/creditTiers';
import { ListWithOverlayWrapper, LoadingIndicatorWrapper } from '../Programs/styles';
import { FirstPencilOfferTypeToggle } from './FirstPencilOfferTypeToggle';
import { FirstPencilProgramsList } from './FirstPencilProgramsList';
import { buildAllTiersRequests, mapQuotesToProgramRows } from './utils';
import type { FirstPencilProgramRow, FirstPencilProgramsOfferType, FirstPencilProgramsTabLeft } from './types';

const NO_PROGRAMS_DESC = 'No Programs Found';
const LOADING_DESC = 'Loading Programs...';

const buildInitialFilters = (
    allTiersMode: boolean,
    customerTier: string | undefined,
    termMonths: number | undefined
): ProgramFilters => {
    const seeded: Partial<Record<keyof ProgramFilters, string[]>> = allTiersMode
        ? { creditTiers: [ALL_CREDIT_TIERS_VALUE] }
        : {
              creditTiers: customerTier ? [customerTier] : [],
              terms: termMonths != null ? [termMonths.toString()] : []
          };
    return { ...DEFAULT_FILTERS, ...seeded };
};

type FirstPencilProgramsPanelProps = {
    offerType: FirstPencilProgramsOfferType;
    onOfferTypeChange: (offerType: FirstPencilProgramsOfferType, tabLeft?: FirstPencilProgramsTabLeft) => void;
};

export const FirstPencilProgramsPanel: FC<FirstPencilProgramsPanelProps> = ({ offerType, onOfferTypeChange }) => {
    const dispatch = useDispatch();

    const allTiersMode = useSelector(featureToggleSelectors.isProgramIncentivesRestorationEnabled);
    const isFiltersEnabled = useSelector(featureToggleSelectors.isFiltersProgramIncentivesEnabled);
    const isPIScreenEnhancementsEnabled = useSelector(featureToggleSelectors.isPIScreenEnhancementsEnabled);
    const manualCreditScore = useSelector(offerReduxSelectors.getManualCreditScore);
    const customerTierScore = useSelector(offerReduxSelectors.getSelectedCreditTierScore);
    const termMonths = useSelector((state) => offerReduxSelectors.getSelectedTerm(state, offerType))?.months;
    const selectedVehicle = useSelector(firstPencilSelectors.getSelectedVehicle);
    const loadedOfferType = useSelector(firstPencilSelectors.getProgramsOfferType);
    const quotes = useSelector(firstPencilSelectors.getProgramQuotes);
    const isLoading = useSelector(firstPencilSelectors.isProgramsLoading);
    const isError = useSelector(firstPencilSelectors.hasProgramsError);
    const selectedProgram = useSelector(firstPencilSelectors.getSelectedProgram);
    const saved = useSelector((state: StateTree) => firstPencilSelectors.getProgramsFilters(state, offerType));

    const dealTier = useMemo(
        () => getCustomerCreditTier(manualCreditScore, customerTierScore.score),
        [manualCreditScore, customerTierScore.score]
    );
    const dealScore = useMemo(
        () => getCustomerCreditScore(manualCreditScore, customerTierScore.score),
        [manualCreditScore, customerTierScore.score]
    );

    const [filters, setFilters] = useState<ProgramFilters>(
        () => saved?.filters ?? buildInitialFilters(allTiersMode, dealTier, termMonths)
    );
    const [appliedFilters, setAppliedFilters] = useState<ProgramFilters>(
        () => saved?.filters ?? buildInitialFilters(allTiersMode, dealTier, termMonths)
    );
    const [filterSlideOutOpen, setFilterSlideOutOpen] = useState(false);
    const [isFilterChipsExpanded, setIsFilterChipsExpanded] = useState(false);
    const [seededDealTier, setSeededDealTier] = useState(() => (saved ? saved.dealTier : dealTier));

    if (seededDealTier !== dealTier) {
        setSeededDealTier(dealTier);
        if (!allTiersMode) {
            const reseedTier = (prev: ProgramFilters): ProgramFilters => ({ ...prev, creditTiers: dealTier ? [dealTier] : [] });
            setFilters(reseedTier);
            setAppliedFilters(reseedTier);
        }
    }

    const requestedTier = allTiersMode ? undefined : appliedFilters.creditTiers[0];

    useEffect(() => {
        if (allTiersMode) {
            dispatch(firstPencilProgramActionCreators.loadFirstPencilProgramQuotes(offerType, buildAllTiersRequests(dealTier)));
            return;
        }
        const creditScoreOverride = getScoreForTier(requestedTier, dealTier, dealScore);
        dispatch(
            firstPencilProgramActionCreators.loadFirstPencilProgramQuotes(offerType, [
                isValidScore(creditScoreOverride) ? { creditScoreOverride } : {}
            ])
        );
    }, [dispatch, offerType, selectedVehicle, allTiersMode, requestedTier, dealTier, dealScore]);

    const isCurrentOfferType = loadedOfferType === offerType;
    const programs: FirstPencilProgramRow[] = useMemo(
        () => (isCurrentOfferType ? mapQuotesToProgramRows(quotes) : []),
        [quotes, isCurrentOfferType]
    );
    const filteredPrograms = useMemo(
        () => filterPrograms(programs, appliedFilters, offerType, isPIScreenEnhancementsEnabled) as FirstPencilProgramRow[],
        [programs, appliedFilters, offerType, isPIScreenEnhancementsEnabled]
    );

    const allFilterOptions = useMemo(
        () => extractAllFilterOptions(programs, isPIScreenEnhancementsEnabled),
        [programs, isPIScreenEnhancementsEnabled]
    );

    const showFilterChips = useMemo(
        () => hasActiveFilters(appliedFilters, offerType, isPIScreenEnhancementsEnabled),
        [appliedFilters, offerType, isPIScreenEnhancementsEnabled]
    );
    const appliedFilterTags = useMemo(
        () => getAppliedFilterTags(appliedFilters, offerType, allFilterOptions, isPIScreenEnhancementsEnabled),
        [appliedFilters, offerType, allFilterOptions, isPIScreenEnhancementsEnabled]
    );
    const visibleFilterTags = isFilterChipsExpanded ? appliedFilterTags : appliedFilterTags.slice(0, MAX_VISIBLE_FILTER_CHIPS);
    const showMoreLessToggle = appliedFilterTags.length > MAX_VISIBLE_FILTER_CHIPS;

    const selectedProgramLabel =
        selectedProgram?.offerType === offerType
            ? formatLenderProgramDisplay(
                  selectedProgram.program.lenderLegalName,
                  selectedProgram.program.lenderProgram?.programName
              )
            : '';

    const showLoading = isLoading || !isCurrentOfferType;
    const showInitialLoading = showLoading && (!allTiersMode || filteredPrograms.length === 0);
    const showNoPrograms = !showLoading && (isError || programs.length === 0);
    const hasNoResults = !showLoading && !showNoPrograms && filteredPrograms.length === 0;

    /** This panel remounts per tab, so it only ever sees its own offerType — fire once per instance. */
    const rrDisplayedFiredRef = useRef(false);
    useEffect(() => {
        if (rrDisplayedFiredRef.current || showLoading) {
            return;
        }
        rrDisplayedFiredRef.current = true;
        logFirstPencilRrDisplayed(offerType, programs.length > 0 ? [offerType] : []);
    }, [showLoading, offerType, programs.length]);

    useEffect(() => {
        if (showLoading || !selectedProgram) {
            return;
        }
        const isPickVisible =
            selectedProgram.offerType === offerType && filteredPrograms.some((row) => row.key === selectedProgram.program.key);
        if (!isPickVisible) {
            dispatch(firstPencilActionCreators.programCleared());
        }
    }, [dispatch, showLoading, selectedProgram, offerType, filteredPrograms]);

    const commitFilters = useCallback((next: ProgramFilters) => {
        setFilters(next);
        setAppliedFilters(next);
    }, []);

    const handleApplyFilters = useCallback(() => commitFilters(filters), [commitFilters, filters]);
    const handleClearFilters = useCallback(
        () =>
            commitFilters(
                allTiersMode
                    ? { ...DEFAULT_FILTERS, creditTiers: [ALL_CREDIT_TIERS_VALUE] }
                    : { ...DEFAULT_FILTERS, creditTiers: dealTier ? [dealTier] : [] }
            ),
        [commitFilters, allTiersMode, dealTier]
    );
    const handleRemoveFilterTag = useCallback(
        (filterKey: keyof ProgramFilters, value: string) => commitFilters(removeFilterValue(appliedFilters, filterKey, value)),
        [appliedFilters, commitFilters]
    );
    const handleOfferTypeChange = useCallback(
        (nextOfferType: FirstPencilProgramsOfferType) =>
            onOfferTypeChange(nextOfferType, { offerType, filters: appliedFilters, dealTier }),
        [onOfferTypeChange, offerType, appliedFilters, dealTier]
    );
    const handleOpenFilters = useCallback(() => {
        setFilters(appliedFilters);
        setFilterSlideOutOpen(true);
    }, [appliedFilters]);
    const handleCloseFilters = useCallback(() => {
        setFilters(appliedFilters);
        setFilterSlideOutOpen(false);
    }, [appliedFilters]);

    return (
        <Box sx={{ marginBottom: '1rem', flex: '1 0 auto' }} data-testid={`first-pencil-programs-panel-${offerType}`}>
            <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', margin: '0.3rem 0 0.5rem' }}>
                <Box
                    data-testid="first-pencil-programs-title"
                    sx={{ display: 'inline-flex', flexDirection: 'column', gap: '0.5rem' }}
                >
                    <Typography variant="h3" sx={{ margin: 0, whiteSpace: 'nowrap' }}>
                        Active Programs
                    </Typography>
                    <FirstPencilOfferTypeToggle value={offerType} onChange={handleOfferTypeChange} />
                </Box>
                {isFiltersEnabled && (
                    <Button
                        buttonStyle="secondary"
                        startIcon={<FunnelIcon />}
                        data-testid="first-pencil-programs-filters-button"
                        size="small"
                        onClick={handleOpenFilters}
                    >
                        Filters
                    </Button>
                )}
            </Box>
            {(selectedProgramLabel || programs.length > 0) && (
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', margin: '1rem 0 1rem' }}>
                    {selectedProgramLabel ? (
                        <Typography variant="body-md" sx={{ margin: 0 }} data-testid="first-pencil-selected-program-name">
                            {selectedProgramLabel}
                        </Typography>
                    ) : (
                        <span />
                    )}
                    {programs.length > 0 && (
                        <Typography variant="body-sm" sx={{ margin: 0 }} data-testid="first-pencil-programs-count">
                            {filteredPrograms.length === programs.length
                                ? `${programs.length} Programs`
                                : `${filteredPrograms.length} from ${programs.length} Programs`}
                        </Typography>
                    )}
                </Box>
            )}
            {showFilterChips && (
                <Box
                    sx={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem', alignItems: 'center', marginBottom: '0.75rem' }}
                    data-testid="first-pencil-applied-filter-tags"
                >
                    {visibleFilterTags.map((tag) => {
                        const isLockedTier = !allTiersMode && tag.filterKey === 'creditTiers';
                        return (
                            <FilterChip
                                key={`${tag.filterKey}-${tag.value}`}
                                data-testid={`first-pencil-filter-chip-${tag.filterKey}-${tag.value}`}
                                label={tag.label}
                                deleteIcon={isLockedTier ? <LockClosedIcon /> : undefined}
                                onClick={isLockedTier ? undefined : () => handleRemoveFilterTag(tag.filterKey, tag.value)}
                                sx={isLockedTier ? { cursor: 'default', '&:active': { transform: 'none' } } : undefined}
                            />
                        );
                    })}
                    {showMoreLessToggle && (
                        <LinkChip
                            label={
                                isFilterChipsExpanded ? 'Show less' : `+${appliedFilterTags.length - MAX_VISIBLE_FILTER_CHIPS} more`
                            }
                            onClick={() => setIsFilterChipsExpanded((prev) => !prev)}
                        />
                    )}
                    <Action data-testid="first-pencil-clear-all-filters-action" onClick={handleClearFilters}>
                        Clear All
                    </Action>
                </Box>
            )}
            {showInitialLoading && (
                <LoadingIndicatorWrapper data-testid="first-pencil-programs-loading-indicator">
                    <Spinner />
                    <Typography variant="h4">{LOADING_DESC}</Typography>
                </LoadingIndicatorWrapper>
            )}
            {showNoPrograms && (
                <Alert
                    type="error"
                    role="alert"
                    title={NO_PROGRAMS_DESC}
                    data-testid="first-pencil-no-programs-alert"
                    sx={{ '.MuiAlertTitle-root': { margin: '0 !important' } }}
                />
            )}
            {hasNoResults && (
                <Alert
                    type="info"
                    role="alert"
                    data-testid="first-pencil-no-filter-results-alert"
                    sx={{ '.MuiAlertTitle-root': { margin: '0 !important' } }}
                >
                    <Typography variant="body-sm">{NO_RESULTS_MESSAGE}</Typography>
                </Alert>
            )}
            {!showInitialLoading && !showNoPrograms && !hasNoResults && (
                <ListWithOverlayWrapper>
                    <FirstPencilProgramsList data={filteredPrograms} offerType={offerType} />
                </ListWithOverlayWrapper>
            )}
            <ProgramsFiltersSlideOut
                isOpen={filterSlideOutOpen}
                onClose={handleCloseFilters}
                hasNoPrograms={programs.length === 0}
                filterOptions={allFilterOptions}
                filters={filters}
                onFiltersChange={setFilters}
                onApplyFilters={handleApplyFilters}
                offerType={offerType}
            />
        </Box>
    );
};
